import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { LiliButton, LiliButtonType, LiliToast, LiliToastDefaultConfig } from '@lili/lili-frontend-common';
import { useStore } from '../../../../../../../../store';
import { AVAILABLE_BILL_MODALS } from '../../consts';
import { ReactComponent as UnpaidIcon } from '../../../../../../../../assets/icons/svg/stamp-icons/ic-stamp-unpaid.svg';
import { markBillAsUnpaidAPIAction } from './api/MarkBillAsUnpaidAPI';
import { isBillStatusPending } from '../../../../utils';
import { BILLS_STATUSES } from '../../../../../../consts';

interface IProps {
  onClose: () => void;
  billId: string;
  billNumber: string;
}

export const MarkBillAsUnpaid = ({ onClose, billId, billNumber }: IProps) => {
  const getPersonalAccount = useStore((state) => state.getPersonalAccount);
  const user = useStore((state) => state.user);
  const removeBillFromList = useStore((state) => state.removeBillFromList);
  const setBillError = useStore((state) => state.setBillError);
  const setSelectedBill = useStore((state) => state.setSelectedBill);
  const showBillDetailsModal = useStore((state) => state.showBillDetailsModal);
  const { accountExternalId } = getPersonalAccount();
  const { customerId } = user;
  const setActiveBillModal = useStore((state) => state.setActiveBillModal);
  const { t } = useTranslation();

  const handleSubmit = async () => {
    setActiveBillModal(AVAILABLE_BILL_MODALS.SPINNER);
    try {
      const response = await markBillAsUnpaidAPIAction(customerId, accountExternalId, billId);
      setActiveBillModal(AVAILABLE_BILL_MODALS.NONE);
      if (isBillStatusPending(response?.status)) {
        toast(<LiliToast
          text={t('BILL_PAY.MARK_AS_UNPAID.SUCCESS', { billNumber, interpolation: { escapeValue: false } })}
          id='cancelAccountant'
        />, {
          ...LiliToastDefaultConfig,
        });
        if (showBillDetailsModal) setSelectedBill(billId, response);
        setTimeout(() => {
          removeBillFromList(BILLS_STATUSES.PAID, billId);
        }, 0);
      }
    } catch (error: any) {
      setBillError(error);
      setActiveBillModal(AVAILABLE_BILL_MODALS.ERROR);
    }
  };

  return (
    <div
      data-testid='BillsHub__Modal__Mark-as-Unpaid'
      className='flex flex-nowrap flex-col h-[340px] w-[343px] bg-bw00 rounded-lg border-[1px] border-bw03 px-[24px] pt-[34px] pb-[24px] box-border'
    >
      <div className='flex justify-center flex-nowrap w-full mb-[25px]'>
        <UnpaidIcon />
      </div>
      <div className='font-metricRMedium text-lg text-bw08 leading-6 mb-[8px] text-center'>
        {t('BILL_PAY.MARK_AS_UNPAID.TITLE')}
      </div>
      <div className='font-metricR text-md text-bw07 leading-6 mb-[22px] text-center'>
        {t('COMPONENTS.MARK_AS_UNPAID.SUBTITLE')}
      </div>
      <div className='flex justify-end'>
        <LiliButton
          testId='billsHub-markAsPaid-buttonCancel'
          id='billsHub-markAsPaid-buttonCancel'
          onClick={onClose}
          type={LiliButtonType.SECONDARY}
          title={t('COMPONENTS.MARK_AS_UNPAID.BUTTON_CANCEL')}
          className='h-[54px] w-[140px]'
        />
        <LiliButton
          testId='billsHub-markAsPaid-buttonUnpaid'
          id='billsHub-markAsPaid-buttonUnpaid'
          onClick={handleSubmit}
          title={t('COMPONENTS.MARK_AS_UNPAID.BUTTON_UNPAID')}
          className='h-[54px] w-[140px] ml-[12px]'
        />
      </div>
    </div>
  );
};
